import { AxiosError } from 'axios'
import {
  getBadgeCounts,
  getConversation,
  getConversationIdFromPathName,
} from 'conversations/modules/conversation/selectors'
import {
  getAttachmentsByMessageId,
  nextActiveCursor,
} from 'conversations/modules/message/selectors'
import { getAppServices } from 'conversations/services'
import MetricUtils, {
  measureInteraction,
  PERFORMANCE_BUCKETS,
} from 'conversations/utils/MetricUtil'
import { isRunningInDesktop } from 'conversations/utils/PlatformUtil'
import { PRESEND_KEY } from 'conversations/utils/presend'
import { Epic } from 'redux-observable'
import { empty, from, merge, of } from 'rxjs'
import {
  catchError,
  debounceTime,
  filter,
  map,
  mergeMap,
  pluck,
  switchMap,
  takeUntil,
} from 'rxjs/operators'
import { decrementBadgeCount, receiveManuallyMarkedAsRead } from 'shared/store/actions/conversation'
import {
  bulkMessageReadAsync,
  fetchAttachmentsAsync,
  fetchMessagesAsync,
  receiveMessage,
  sendMessageAsync,
} from 'shared/store/actions/message'
import { performanceTracking } from 'shared/store/actions/performanceTracking'
import { onElectronFocus, onWindowFocus } from 'shared/store/actions/platform'
import { track } from 'shared/store/actions/tracking'
import { Attachment } from 'shared/typings/conversations/Attachment'
import { Message } from 'shared/typings/messages/Message'

import { CLUSTER, socketReconnect } from '@driftt/drift-ws'

import { isActionOf, RootAction, RootState, Services } from 'typesafe-actions'

import { getUnreadMessagesForConversation } from '../conversation/selectors'

import * as api from './api'

const DEFAULT_PAGINATION_LIMIT = 35

export const fetchMessages: Epic<RootAction, RootAction, RootState, Services> = (
  action$,
  state
) => {
  return action$.pipe(
    filter(isActionOf(fetchMessagesAsync.request)),
    mergeMap(({ payload: { conversationId, loadMore, limit } }) =>
      from(
        api.fetchMessages(
          conversationId,
          limit ? limit : DEFAULT_PAGINATION_LIMIT,
          loadMore ? nextActiveCursor(state.value, conversationId) : undefined
        )
      ).pipe(
        map((paginatedMessages) =>
          fetchMessagesAsync.success({
            paginatedMessages,
            conversationId,
            loadMore,
          })
        ),
        catchError((error: AxiosError) =>
          of(
            fetchMessagesAsync.failure({
              error,
              conversationId,
            })
          )
        ),
        takeUntil(action$.pipe(filter(isActionOf(fetchMessagesAsync.cancel))))
      )
    )
  )
}

export const bulkReadMessagesBadgeCount: Epic<RootAction, RootAction, RootState, Services> = (
  action$
) => {
  return action$.pipe(
    filter(isActionOf([bulkMessageReadAsync.success, receiveManuallyMarkedAsRead])),
    filter(({ payload }) => {
      const { inboxId, conversationId, prevBadgeCounts } = payload
      return !!inboxId && !!prevBadgeCounts[inboxId] && !!prevBadgeCounts[inboxId][conversationId]
    }),
    mergeMap(({ payload: { conversationId, inboxId } }) => [
      decrementBadgeCount({ conversationId, inboxId }),
    ])
  )
}

export const bulkReadMessages: Epic<RootAction, RootAction, RootState, Services> = (
  action$,
  state
) => {
  return action$.pipe(
    filter(isActionOf(bulkMessageReadAsync.request)),
    debounceTime(500),
    map(({ payload: { conversationId } }) => {
      return {
        conversationId,
        unreadMessages: getUnreadMessagesForConversation(state.value, conversationId),
      }
    }),
    mergeMap(({ unreadMessages, conversationId }) => {
      if (unreadMessages.length < 1) {
        return of(
          bulkMessageReadAsync.cancel({
            conversationId,
          })
        )
      }
      return from(getAppServices().badges.isWindowVisible()).pipe(
        mergeMap((isVisible) => {
          if (isVisible) {
            const ids = unreadMessages.map((i) => {
              return i.id
            })
            const convo = getConversation(conversationId)(state.value)
            const badgeCounts = getBadgeCounts()(state.value)
            return from(api.bulkMarkRead(conversationId, ids)).pipe(
              mergeMap(() => [
                bulkMessageReadAsync.success({
                  conversationId,
                  inboxId: convo?.inboxId,
                  prevBadgeCounts: badgeCounts,
                }),
              ])
            )
          } else {
            return of(
              bulkMessageReadAsync.cancel({
                conversationId,
              })
            )
          }
        })
      )
    }),
    catchError((error: Error) => of(bulkMessageReadAsync.failure(error)))
  )
}

export const fetchAttachments: Epic<RootAction, RootAction, RootState, Services> = (
  action$,
  state
) => {
  return action$.pipe(
    filter(isActionOf(fetchAttachmentsAsync.request)),
    filter(({ payload }) => getAttachmentsByMessageId(payload.messageId)(state.value).length === 0),
    mergeMap(({ payload }) =>
      from(api.fetchAttachments(payload.attachmentIds)).pipe(
        map((attachments: Attachment[]) =>
          fetchAttachmentsAsync.success({
            attachments,
            ...payload,
          })
        ),
        catchError((error) => of(fetchAttachmentsAsync.failure(error))),
        takeUntil(action$.pipe(filter(isActionOf(fetchAttachmentsAsync.cancel))))
      )
    )
  )
}

// onElectronFocus is more reliable for electron (onWindowFocus doesn't cover all cases)
export const readMessagesOnWindowFocus: Epic<RootAction, RootAction, RootState, Services> = (
  action$
) => {
  return action$.pipe(
    filter(
      (action$: RootAction) =>
        (isActionOf(onWindowFocus, action$) && !isRunningInDesktop) ||
        isActionOf(onElectronFocus, action$)
    ),
    switchMap(() => {
      const conversationId = getConversationIdFromPathName()
      if (!conversationId) {
        return of(track({ name: 'App Focused' }))
      }
      return merge(
        of(
          bulkMessageReadAsync.request({
            conversationId,
          })
        ),
        of(track({ name: 'App Focused' }))
      )
    })
  )
}

export const sendMessage: Epic<RootAction, RootAction, RootState, Services> = (action$) => {
  return action$.pipe(
    filter(isActionOf(sendMessageAsync.request)),
    mergeMap(({ payload }) =>
      from(
        api.sendMessage(
          {
            attributes: payload.attributes,
            body: payload.body,
            type: payload.type,
            attachments: payload.attachments,
            contentType: payload.contentType,
          },
          payload.conversationId
        )
      ).pipe(
        mergeMap((message) => {
          const interaction = measureInteraction(
            'drift_convo_desktop_new_message_render_time',
            true
          )
          return [
            sendMessageAsync.success(message),
            track({ name: 'Message Sent', properties: { conversationId: message.conversationId } }),
            performanceTracking({ interaction }),
          ]
        }),
        catchError((error) => of(sendMessageAsync.failure({ error: error, message: payload }))),
        takeUntil(action$.pipe(filter(isActionOf(sendMessageAsync.cancel))))
      )
    )
  )
}

// The scrappy way of coordinating these timestamps
// I tried doing it with just observables but it was overly complicated
const messageTimeStamps: { [key: string]: number } = {}

export const measureMessageRtt: Epic<RootAction, RootAction, RootState, Services> = (action$) => {
  const getPresendKey = (message: Message) => message.attributes && message.attributes[PRESEND_KEY]

  action$.pipe(filter(isActionOf(sendMessageAsync.request))).subscribe(({ payload }) => {
    const presendKey = getPresendKey(payload)
    if (presendKey) {
      messageTimeStamps[presendKey] = Date.now()
    }
  })

  action$.pipe(filter(isActionOf(receiveMessage))).subscribe(({ payload }) => {
    const presendKey = getPresendKey(payload)
    if (presendKey && messageTimeStamps[presendKey]) {
      const rtt = Date.now() - messageTimeStamps[presendKey]
      MetricUtils.write('desktop-app-rtt', rtt, 'bucket', {
        buckets: PERFORMANCE_BUCKETS,
      })
      delete messageTimeStamps[presendKey]
    }
  })

  return empty()
}

export const refetchCurrentMessagesOnReconnect: Epic<
  RootAction,
  RootAction,
  RootState,
  Services
> = (action$) => {
  return action$.pipe(
    filter(isActionOf(socketReconnect)),
    pluck('payload'),
    filter((cluster) => cluster === CLUSTER.CHAT),
    debounceTime(500), // avoid flapping connections
    map(() => {
      const conversationId = getConversationIdFromPathName()
      if (!conversationId) {
        return fetchMessagesAsync.cancel()
      }
      return fetchMessagesAsync.request({
        loadMore: false,
        conversationId: Number(conversationId),
      })
    })
  )
}
